import { useState, useRef, useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import PostRegistrationModal from './PostRegistrationModal';
import { BRAND } from '@/brand.config';
import { captureException, captureMessage } from '@/lib/sentry';

type FieldName = 'firstName' | 'lastName' | 'email';
type FieldErrors = Partial<Record<FieldName, string>>;
type SignupStatus = 'created' | 'reactivated' | 'existing';
type ResendState = 'idle' | 'submitting' | 'queued' | 'recent' | 'error';

interface RegistrationFormProps {
  ctaButtonText: string;
  successHeading: string;
  successSubtext: string;
  activeMeetingId?: string;
  /** Recorded as `subscribers.source` and on the audit event. Defaults to 'Landing Page'. */
  source?: string;
}

interface SignupContext {
  signupUrl: string;
  signupReferrer: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POST_SIGNUP_MODAL_DELAY_MS = 400;

function captureSignupContext(): SignupContext {
  if (typeof window === 'undefined') {
    return { signupUrl: '', signupReferrer: '' };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    signupUrl: window.location.href,
    signupReferrer: document.referrer || '',
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
  };
}

export function validateForm(values: FormValues): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.firstName.trim()) errors.firstName = 'Required';
  const trimmedEmail = values.email.trim();
  if (!trimmedEmail) {
    errors.email = 'Required';
  } else if (!EMAIL_REGEX.test(trimmedEmail)) {
    errors.email = 'Enter a valid email';
  }
  return errors;
}

export default function RegistrationForm({
  ctaButtonText,
  successHeading,
  successSubtext,
  activeMeetingId,
  source = BRAND.defaultSignupSource,
}: RegistrationFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupStatus, setSignupStatus] = useState<SignupStatus | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [resendState, setResendState] = useState<ResendState>('idle');
  const [resendError, setResendError] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);
  // Atomic guard against duplicate submits — React state batching means
  // isSubmitting can race with rapid double-clicks; a ref is bulletproof.
  const submittingRef = useRef(false);
  const signupContext = useMemo(() => captureSignupContext(), []);

  useEffect(() => {
    if (signupStatus && successRef.current) {
      successRef.current.focus();
    }
  }, [signupStatus]);

  const clearFieldError = (field: FieldName) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setServerError(null);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (submittingRef.current) return;

    const fieldErrors = validateForm({ firstName, lastName, email });
    setErrors(fieldErrors);
    setServerError(null);
    if (Object.keys(fieldErrors).length > 0) return;

    submittingRef.current = true;
    setIsSubmitting(true);

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedEmail = email.trim().toLowerCase();

    try {
      const { data, error } = await supabase.functions.invoke('signup', {
        body: {
          firstName: trimmedFirst,
          lastName: trimmedLast,
          email: trimmedEmail,
          source,
          signupUrl: signupContext.signupUrl,
          signupReferrer: signupContext.signupReferrer,
          utmSource: signupContext.utmSource,
          utmMedium: signupContext.utmMedium,
          utmCampaign: signupContext.utmCampaign,
        },
      });

      if (error || !data?.ok) {
        const message =
          data?.error?.message ||
          (error instanceof Error ? error.message : null) ||
          'Could not save your info. Please try again.';
        // Server-side rejection or function failure — capture to Sentry
        // as a message rather than exception (we recovered gracefully).
        captureMessage(`Signup function returned ok:false: ${message}`, 'warning', {
          source: 'registration_form',
          errorCode: data?.error?.code,
          response: data,
          invokeError: error instanceof Error ? error.message : error,
        });
        setServerError(message);
        return;
      }

      const rawStatus = data?.data?.status as string | undefined;
      const status: SignupStatus =
        rawStatus === 'created' || rawStatus === 'reactivated' || rawStatus === 'existing'
          ? rawStatus
          : 'created';
      if (rawStatus !== status) {
        // Server returned something we don't recognize. Render the
        // friendly success state (graceful degrade) but flag it so a
        // backend regression doesn't go silent.
        captureMessage(
          `signup returned unexpected status: ${rawStatus ?? '(missing)'}`,
          'warning',
          { source: 'registration_form', response: data },
        );
      }
      setSubmittedEmail(trimmedEmail);
      setSignupStatus(status);

      // The signup edge function queues the welcome email server-side. The
      // form intentionally doesn't try to send anything itself — a closed
      // tab, flaky network, or Resend outage can't lose the email.
      // Skip the post-signup modal for returning subscribers; the
      // 'existing' branch has its own UX (resend button) and the modal
      // would confuse users who just clicked a redundant signup.
      if (
        status !== 'existing' &&
        activeMeetingId &&
        !localStorage.getItem(`signup_modal_seen_${activeMeetingId}`)
      ) {
        setTimeout(() => setShowModal(true), POST_SIGNUP_MODAL_DELAY_MS);
      }
    } catch (err) {
      console.error('[Form] Signup failed:', err);
      captureException(err, { source: 'registration_form_submit' });
      setServerError('Something went wrong. Please try again.');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!submittedEmail) return;
    if (resendState === 'submitting' || resendState === 'queued' || resendState === 'recent') {
      return;
    }
    setResendState('submitting');
    setResendError(null);
    try {
      const { data, error } = await supabase.functions.invoke('resend-welcome', {
        body: { email: submittedEmail },
      });
      if (error || !data?.ok) {
        const code = data?.error?.code;
        const message =
          code === 'unsubscribed'
            ? 'This email has been unsubscribed. Sign up again to re-enable.'
            : data?.error?.message ||
              (error instanceof Error ? error.message : null) ||
              'Could not resend. Please try again.';
        captureMessage(`resend-welcome returned ok:false: ${message}`, 'warning', {
          source: 'registration_form_resend',
          errorCode: code,
          response: data,
          invokeError: error instanceof Error ? error.message : error,
        });
        setResendError(message);
        setResendState('error');
        return;
      }
      // 'queued' = a fresh row went into the welcome_email_queue.
      // 'recent' = collapses the rate-limited and not-found cases — same UX.
      const rawResendStatus = data.status as string | undefined;
      if (rawResendStatus !== 'queued' && rawResendStatus !== 'recent') {
        captureMessage(
          `resend-welcome returned unexpected status: ${rawResendStatus ?? '(missing)'}`,
          'warning',
          { source: 'registration_form_resend', response: data },
        );
      }
      setResendState(rawResendStatus === 'queued' ? 'queued' : 'recent');
    } catch (err) {
      console.error('[Form] Resend failed:', err);
      captureException(err, { source: 'registration_form_resend' });
      setResendError('Something went wrong. Please try again.');
      setResendState('error');
    }
  };

  if (signupStatus === 'existing') {
    const headline = "Looks like you're already signed up.";
    const subtext =
      resendState === 'queued'
        ? 'Sent. Check your inbox in a minute.'
        : resendState === 'recent'
          ? "We sent one recently — check your spam folder if you don't see it."
          : 'Want us to resend the link?';
    const showButton = resendState !== 'queued' && resendState !== 'recent';

    return (
      <div
        ref={successRef}
        tabIndex={-1}
        className="animate-reveal-blur py-4 outline-none"
      >
        <p className="font-serif text-xl font-bold text-foreground">{headline}</p>
        <p className="mt-2 font-sans text-sm text-muted-foreground">{subtext}</p>
        {showButton && (
          <Button
            type="button"
            onClick={handleResend}
            disabled={resendState === 'submitting'}
            className="mt-4 w-full rounded-sm bg-primary px-8 py-6 font-serif text-base font-bold text-primary-foreground transition-all duration-300 hover:bg-primary/90 disabled:opacity-50 sm:text-lg"
          >
            {resendState === 'submitting' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resending...
              </>
            ) : (
              'Resend the link'
            )}
          </Button>
        )}
        {resendError && (
          <p
            role="alert"
            className="mt-3 text-center text-sm font-serif italic text-primary"
          >
            {resendError}
          </p>
        )}
      </div>
    );
  }

  if (signupStatus === 'created' || signupStatus === 'reactivated') {
    return (
      <>
        <div
          ref={successRef}
          tabIndex={-1}
          className="animate-reveal-blur py-4 outline-none"
        >
          <p className="font-serif text-xl font-bold text-foreground">
            {successHeading}
          </p>
          <p className="mt-2 font-sans text-sm text-muted-foreground">
            {successSubtext}
          </p>
        </div>
        {activeMeetingId && submittedEmail && (
          <PostRegistrationModal
            open={showModal}
            onClose={() => setShowModal(false)}
            meetingId={activeMeetingId}
            email={submittedEmail}
          />
        )}
      </>
    );
  }

  const fieldClass = (field: FieldName) =>
    cn(
      'rounded-sm bg-card border-border text-foreground placeholder:text-muted-foreground',
      errors[field] && 'border-primary',
    );

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="space-y-3 px-[10%] sm:px-[15%]"
    >
      <div className="animate-reveal-blur-delay-3 space-y-3">
        <div>
          <label htmlFor="firstName" className="sr-only">First name</label>
          <Input
            id="firstName"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              clearFieldError('firstName');
            }}
            placeholder="First name"
            aria-invalid={!!errors.firstName}
            className={fieldClass('firstName')}
          />
          {errors.firstName && (
            <p className="mt-1 text-xs font-serif italic text-primary">
              {errors.firstName}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="lastName" className="sr-only">Last name</label>
          <Input
            id="lastName"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              clearFieldError('lastName');
            }}
            placeholder="Last name"
            aria-invalid={!!errors.lastName}
            className={fieldClass('lastName')}
          />
          {errors.lastName && (
            <p className="mt-1 text-xs font-serif italic text-primary">
              {errors.lastName}
            </p>
          )}
        </div>
      </div>
      <div className="animate-reveal-blur-delay-4">
        <label htmlFor="email" className="sr-only">Email address</label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearFieldError('email');
          }}
          placeholder="Email"
          aria-invalid={!!errors.email}
          className={fieldClass('email')}
        />
        {errors.email && (
          <p className="mt-1 text-xs font-serif italic text-primary">
            {errors.email}
          </p>
        )}
      </div>
      {serverError && (
        <p
          role="alert"
          className="text-center text-sm font-serif italic text-primary"
        >
          {serverError}
        </p>
      )}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="animate-reveal-blur-delay-5 w-full rounded-sm bg-primary px-8 py-6 font-serif text-base font-bold text-primary-foreground transition-all duration-300 hover:bg-primary/90 disabled:opacity-50 sm:text-lg"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          ctaButtonText
        )}
      </Button>
    </form>
  );
}
